import React, { useEffect, useState } from "react";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Text,
	useToast,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Button } from "newui/components/Button";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import { FileUpload } from "newui/components/FileUpload";
import { FILE_UPLOAD_TYPES } from "newui/components/FileUpload/constants/fileUploadTypes";
import type { UploadedFile } from "newui/components/FileUpload/types/UploadedFile.type";
import type { AllowedFileType } from "newui/components/FileUpload/types/AllowedFileType.types";
import type { UploadFile } from "types/storage.types";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { SubmissionSuccessfulEmailContent } from "components/email-template/SubmissionSuccessfulEmailContent";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import { useUpsertAwards } from "../../../../hooks/useUpsertAwards";
import type { AwardAttachment, AwardRow } from "../../../../types/award.type";
import { ConfirmDecisionDialog } from "./ConfirmDecisionDialog";
import {
	AWARD_LABEL,
	CONFIRM_DECISION_BODY_PREFERRED,
	CONFIRM_DECISION_CONFIRM_LABEL,
	CONFIRM_DECISION_TITLE_PREFERRED,
	CONTRACT_ACCEPTED_EXTENSIONS,
	CONTRACT_MAX_SIZE_MB,
	isAwardDecisionFinal,
	REASON_MAX_LENGTH,
} from "../awardDecisions";

interface AwardFormValues {
	reason: string;
}

const validationSchema = yup.object().shape({
	reason: yup
		.string()
		.max(REASON_MAX_LENGTH, `Reason must be ${REASON_MAX_LENGTH} characters or fewer`),
});

const ACCEPTED_FILE_TYPES: AllowedFileType[] = [...CONTRACT_ACCEPTED_EXTENSIONS];

/** Map a persisted award attachment to the FileUpload `initialValue` shape so
 * previously-uploaded files rehydrate (display + download) when the drawer is
 * reopened. */
const mapAttachmentToUploadFile = (a: AwardAttachment): UploadFile => ({
	id: a.file_id,
	file_name: a.file_name,
	file_path: a.file_path ?? "",
	file_size: a.file_size ?? 0,
	file_type: a.file_type ?? undefined,
});

/** Collect completed uploads (server-assigned id present) back into the
 * attachment list used for the email preview and the decision payload. */
const mapUploadedToAttachments = (files: UploadedFile[]): AwardAttachment[] =>
	files
		.filter((f) => f.id)
		.map((f) => ({
			file_id: f.id as number,
			file_name: f.name,
			file_path: f.path ?? f.storage_path ?? null,
			file_size: f.size ?? null,
			file_type: f.type ?? null,
			storage_path: f.storage_path ?? f.path ?? null,
		}));

interface AwardDrawerProps {
	evalId?: number;
	isOpen: boolean;
	rfx: Rfx | null;
	rfxId?: number;
	supplier: AwardRow | null;
	onClose: () => void;
}

export const AwardDrawer: React.FC<AwardDrawerProps> = ({
	isOpen,
	supplier,
	rfx,
	rfxId,
	evalId,
	onClose,
}) => {
	const toast = useToast();
	const [attachments, setAttachments] = useState<AwardAttachment[]>([]);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const { rfxMail, handleAdditionalMessageChange } = useRfxMail(rfx);

	const upsertAwards = useUpsertAwards(rfxId, evalId);

	// Seed from any persisted attachments whenever the drawer (re)opens for a
	// supplier so previously-uploaded files rehydrate instead of disappearing.
	useEffect(() => {
		if (isOpen) {
			setAttachments(supplier?.attachments ?? []);
			setIsConfirmOpen(false);
		}
	}, [isOpen, supplier?.rfx_invite_id, supplier?.attachments]);

	// FileUpload owns the chunked upload + per-row lifecycle; we just mirror the
	// completed uploads (those with a server id) into the attachment list used by
	// the email preview and the decision payload.
	const handleFilesChange = (files: UploadedFile[]) => {
		setAttachments(mapUploadedToAttachments(files));
	};

	const handleCancel = () => {
		// Cancelling the drawer discards any uploaded file ids (AC4).
		setAttachments([]);
		setIsConfirmOpen(false);
		onClose();
	};

	const submitAward = async (reason: string) => {
		if (!supplier) {
			return;
		}
		try {
			await upsertAwards.mutateAsync({
				decisions: [
					{
						rfx_invite_id: supplier.rfx_invite_id,
						award_status: "awarded",
						comment: reason.trim() ? reason.trim() : null,
						attachment_file_ids: attachments.map((a) => a.file_id),
					},
				],
			});
			setIsConfirmOpen(false);
			toast({
				title: `Marked ${AWARD_LABEL}`,
				description: `${supplier.supplier.name} has been marked ${AWARD_LABEL}.`,
				status: "success",
				duration: 4000,
				isClosable: true,
			});
			onClose();
		} catch (error) {
			setIsConfirmOpen(false);
			if (isAwardDecisionFinal(error)) {
				// useUpsertAwards.onError invalidates the awards + completion caches
				// on this 409, so the table refreshes to the already-final state.
				toast({
					title: "Decision already final",
					description:
						"This decision has already been finalised. The list has been refreshed.",
					status: "warning",
					duration: 5000,
					isClosable: true,
				});
			}
		}
	};

	if (!supplier) {
		return null;
	}

	return (
		<Drawer placement="right" isOpen={isOpen} onClose={handleCancel} size="xl">
			<DrawerOverlay />
			<DrawerContent maxWidth="1100px" data-testid="award-drawer">
				<DrawerHeader className="border-b-2">
					<Flex justifyContent="space-between" align="center">
						<div>
							<p className="text-xl font-semibold text-bg-brand-solid">
								{AWARD_LABEL} Supplier
							</p>
							<p className="text-sm font-normal text-gray-600">
								Marking <strong>{supplier.supplier.name}</strong> as{" "}
								{AWARD_LABEL}. Add an optional reason and contract, then confirm.
								You can preview the notification email on the right.
							</p>
						</div>
						<Button
							btntype="plain-icon"
							type="button"
							onClick={handleCancel}
							aria-label="Close"
							data-testid="award-drawer-close"
						>
							<FiX />
						</Button>
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<Formik<AwardFormValues>
						initialValues={{ reason: "" }}
						validationSchema={validationSchema}
						onSubmit={() => {
							// Submission is gated by the confirmation dialog.
							setIsConfirmOpen(true);
						}}
					>
						{({ values, errors, setFieldValue }) => (
							<Form>
								<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
									<div className="lg:col-span-1 space-y-6">
										<TextAreaWithLabel
											label="Reason (optional)"
											name="reason"
											placeholder="Enter an optional reason for this decision..."
											maxLength={REASON_MAX_LENGTH}
											showCharacterCount
											value={values.reason}
											errorText={errors.reason}
											hasError={!!errors.reason}
											testId="award-reason"
											onChange={(e) => {
												setFieldValue("reason", e.target.value);
												handleAdditionalMessageChange(e.target.value);
											}}
										/>

										<div data-testid="contract-upload">
											<p className="text-sm font-medium text-gray-700 mb-1">
												Contract &amp; attachment files{" "}
												<span className="text-primary-600 font-semibold">
													(recommended)
												</span>
											</p>
											<p className="text-xs text-gray-500 mb-2">
												Accepted: {CONTRACT_ACCEPTED_EXTENSIONS.join(", ")} · max{" "}
												{CONTRACT_MAX_SIZE_MB}MB each. Optional — you can upload
												multiple files; all are included in the email.
											</p>

											{/* Removing a not-yet-sent file just drops it from the
											  * list; the orphaned upload (fileable_id = 0) is swept by
											  * a cron, so no hard-delete call is needed here. */}
											<FileUpload
												allowFileTypes={ACCEPTED_FILE_TYPES}
												extraData={{
													rfq_id: rfxId,
													evalId,
													rfx_invite_id: supplier.rfx_invite_id,
												}}
												initialValue={(supplier?.attachments ?? []).map(
													mapAttachmentToUploadFile
												)}
												maxFileSizeMB={CONTRACT_MAX_SIZE_MB}
												multiple
												onChange={handleFilesChange}
												supportsHardDelete={false}
												uploadType={FILE_UPLOAD_TYPES.RFX_AWARD_CONTRACT}
											/>
										</div>

										<Flex gap={3} className="!w-full">
											<Button
												btntype="base"
												type="button"
												width="100%"
												onClick={handleCancel}
												data-testid="award-cancel"
											>
												Cancel
											</Button>
											<Button
												type="submit"
												width="100%"
												data-testid="award-confirm"
											>
												Send Email
											</Button>
										</Flex>
									</div>

									<div className="lg:col-span-2">
										<Text className="mb-3 text-lg font-semibold text-primary-900">
											{AWARD_LABEL} Supplier Email Preview
										</Text>
										{rfxMail && (
											<EmailPreview>
												<SubmissionSuccessfulEmailContent
													rfxMail={rfxMail}
													decisionFiles={attachments.map((a) => ({
														name: a.file_name,
													}))}
												/>
											</EmailPreview>
										)}
									</div>
								</div>

								<ConfirmDecisionDialog
									isOpen={isConfirmOpen}
									isSubmitting={upsertAwards.isPending}
									title={CONFIRM_DECISION_TITLE_PREFERRED}
									body={CONFIRM_DECISION_BODY_PREFERRED}
									confirmLabel={CONFIRM_DECISION_CONFIRM_LABEL}
									onCancel={() => setIsConfirmOpen(false)}
									onConfirm={() => submitAward(values.reason)}
								/>
							</Form>
						)}
					</Formik>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
